/**
 * Self-service personal-invite onboarding wizard.
 *
 * Mounted at /onboard/:token. Token is in the URL path and is the sole
 * credential — no login required. Multi-step form with autosave on step
 * transition, plus document upload when the admin requested any. Terminal-
 * state screens for the four 410 paths and a generic invalid-link page
 * for 404.
 *
 * Mobile-first; mirrors the styling shell of ActivationFlow.
 *
 * This file owns the personal-invite SPECIFIC behaviors only — prefill
 * from invite, document upload, draft autosave, and the invite-* terminal
 * map. The wizard chrome, step bodies, and TerminalScreen come from
 * onboardingWizard.jsx so the personal-invite and shareable-link flows
 * are guaranteed to render the same wizard.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  loadInvite,
  saveDraft,
  submitOnboarding,
  uploadDocument,
  deleteDocument,
  fetchDocumentBlob,
} from '../../api/onboardingApi.js';
import { normalizePhone } from '../../utils/phone.js';
import { EMPTY_PAYLOAD, mergePayload, errMessage } from './onboardingShared.js';
import {
  StepWelcome,
  StepContact,
  StepVehicle,
  StepReview,
  TerminalScreen,
  WizardChrome,
} from './onboardingWizard.jsx';
import './OnboardingFlow.less';

// Step list is derived per-invite. By default (requestedDocuments=[]) the
// Documents step is skipped — onboarding stays under a minute. Admins can
// opt-in to documents per invite for fleet / corporate / KYC flows.
const BASE_STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'contact', label: 'Contact' },
  { id: 'vehicle', label: 'Vehicle' },
];
const DOCUMENTS_STEP = { id: 'documents', label: 'Documents' };
const REVIEW_STEP    = { id: 'review',    label: 'Review' };

function buildSteps(requestedDocuments) {
  const docsRequested = Array.isArray(requestedDocuments) && requestedDocuments.length > 0;
  const ordered = [...BASE_STEPS];
  if (docsRequested) ordered.push(DOCUMENTS_STEP);
  ordered.push(REVIEW_STEP);
  return ordered.map((s, i) => ({ ...s, number: i + 1 }));
}

const DOC_TYPES = [
  { value: 'id_card', label: 'ID card' },
  { value: 'driving_licence', label: 'Driving licence' },
  { value: 'vehicle_registration', label: 'Vehicle registration' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

function OnboardingFlow() {
  const { token: rawToken } = useParams();
  const token = decodeURIComponent(rawToken || '');

  const [phase, setPhase] = useState('loading'); // loading | form | submitted | approved | terminal
  const [terminal, setTerminal] = useState(null);
  const [invite, setInvite] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [payload, setPayload] = useState(EMPTY_PAYLOAD);
  const [step, setStep] = useState(1);
  const steps = useMemo(
    () => buildSteps(invite?.requestedDocuments),
    [invite?.requestedDocuments],
  );
  const currentStep = steps[step - 1] || steps[0];
  const [savingDraft, setSavingDraft] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [pendingDocType, setPendingDocType] = useState('id_card');
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);
  const dirtyRef = useRef(false);

  // Hydrate
  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    loadInvite(token)
      .then((data) => {
        if (cancelled) return;
        if (data?.ok) {
          setInvite(data.invite);
          setDocuments(data.documents || []);
          const initial = data.submission?.payload_json;
          if (initial && typeof initial === 'object' && Object.keys(initial).length > 0) {
            setPayload(mergePayload(EMPTY_PAYLOAD, initial));
          } else {
            // Build initial contact prefill from the invite. Prefer split
            // first/last name; fall back to any admin-supplied prefill JSON.
            const invitePrefill = data.invite?.prefill || {};
            const inviteContact = {
              firstName: data.invite?.inviteeFirstName || invitePrefill?.contact?.firstName || '',
              lastName:  data.invite?.inviteeLastName  || invitePrefill?.contact?.lastName  || '',
              email:     data.invite?.inviteeEmail    || invitePrefill?.contact?.email    || '',
              phone:     data.invite?.inviteePhone    || invitePrefill?.contact?.phone    || '',
            };
            setPayload(
              mergePayload(EMPTY_PAYLOAD, {
                ...invitePrefill,
                contact: { ...invitePrefill.contact, ...inviteContact },
              }),
            );
          }
          setPhase('form');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const body = err?.response?.data || {};
        const code = body.error;
        if (status === 410) {
          const map = {
            invite_expired:   { title: 'Link expired', message: 'This onboarding link has expired. Contact your administrator for a new one.', kind: 'warn' },
            invite_cancelled: { title: 'Invite cancelled', message: 'This invite has been cancelled. Contact your administrator if this is a mistake.', kind: 'warn' },
            invite_submitted: { title: "Thanks — we've got your submission", message: 'An administrator is reviewing your details. You will receive an email once it has been processed.', kind: 'success' },
            invite_approved:  { title: 'You are all set', message: 'Your onboarding has been approved. Check your email for login instructions.', kind: 'success' },
            invite_rejected:  { title: 'Update from your administrator', message: 'Your onboarding could not be approved at this time. Please contact your administrator for details.', kind: 'warn' },
            charger_inactive: { title: 'Link no longer in service', message: 'Please contact your administrator.', kind: 'warn' },
          };
          const entry = map[code] || { title: 'Link no longer valid', message: body.message || 'Please contact your administrator.', kind: 'warn' };
          setTerminal(entry);
          setPhase('terminal');
        } else if (status === 404 || status === 400) {
          setTerminal({ title: 'Invalid link', message: 'This onboarding link is not valid. Make sure you used the full link from your invite email.', kind: 'warn' });
          setPhase('terminal');
        } else {
          setTerminal({ title: 'Could not load invite', message: errMessage(err, 'Please retry in a moment.'), kind: 'warn' });
          setPhase('terminal');
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  // ── Field updates ────────────────────────────────────────────────
  const updateField = useCallback((block, field, value) => {
    setPayload((p) => ({ ...p, [block]: { ...p[block], [field]: value } }));
    dirtyRef.current = true;
  }, []);

  const setAgreedToTerms = useCallback((checked) => {
    setPayload((p) => ({ ...p, agreedToTerms: !!checked }));
    dirtyRef.current = true;
    setValidationErrors((errs) => errs.filter((e) => e.key !== 'agreedToTerms'));
  }, []);

  // Autosave on step transition (only when dirty)
  const persistDraft = useCallback(async () => {
    if (!dirtyRef.current) return;
    setSavingDraft(true);
    try {
      await saveDraft(token, { ...payload, step });
      setSavedAt(new Date());
      dirtyRef.current = false;
    } catch (err) {
      // Surface gently — don't block step transition on a network blip.
      console.warn('[onboarding] draft save failed', errMessage(err));
    } finally {
      setSavingDraft(false);
    }
  }, [token, payload, step]);

  const goNext = useCallback(async () => {
    await persistDraft();
    setStep((s) => Math.min(steps.length, s + 1));
  }, [persistDraft, steps.length]);

  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);

  const jumpToStep = useCallback(
    (stepId) => {
      const idx = steps.findIndex((s) => s.id === stepId);
      if (idx >= 0) setStep(idx + 1);
    },
    [steps],
  );

  // ── Documents ────────────────────────────────────────────────────
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const data = await uploadDocument(token, file, pendingDocType);
      if (data?.ok) {
        setDocuments((d) => [data.document, ...d]);
      } else {
        setUploadError(data?.message || 'Upload failed');
      }
    } catch (err) {
      setUploadError(errMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDeleteDoc = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      const data = await deleteDocument(token, docId);
      if (data?.ok) setDocuments((d) => d.filter((x) => x.id !== docId));
    } catch (err) {
      setUploadError(errMessage(err, 'Could not remove document'));
    }
  };

  const onPreviewDoc = async (docId) => {
    try {
      const { blob, mimeType } = await fetchDocumentBlob(token, docId);
      const url = URL.createObjectURL(new Blob([blob], { type: mimeType }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setUploadError(errMessage(err, 'Could not load preview'));
    }
  };

  // ── Submit ───────────────────────────────────────────────────────
  const computeMissingFields = useCallback(() => {
    const errs = [];
    if (!payload.contact.firstName?.trim()) errs.push({ key: 'firstName', label: 'First name', stepId: 'contact' });
    if (!payload.contact.lastName?.trim())  errs.push({ key: 'lastName',  label: 'Last name',  stepId: 'contact' });
    if (!payload.contact.email?.trim())     errs.push({ key: 'email',     label: 'Email',      stepId: 'contact' });
    // Phone stays optional; a number that was entered must be a valid one.
    if (payload.contact.phone?.trim() && !normalizePhone(payload.contact.phone).ok) errs.push({ key: 'phone', label: 'Phone (enter a valid number)', stepId: 'contact' });
    if (!payload.vehicle.plate?.trim())     errs.push({ key: 'plate',     label: 'Licence plate', stepId: 'vehicle' });
    if (!payload.agreedToTerms)             errs.push({ key: 'agreedToTerms', label: 'Declaration confirmation', stepId: 'review' });
    return errs;
  }, [payload]);

  const handleSubmit = async () => {
    if (submitting) return;
    const missing = computeMissingFields();
    setValidationErrors(missing);
    setSubmitError(null);
    if (missing.length > 0) return; // Inline summary tells the user what to fix.
    setSubmitting(true);
    try {
      const data = await submitOnboarding(token, payload);
      if (data?.ok) {
        setPhase(data.autoApproved ? 'approved' : 'submitted');
      } else {
        setSubmitError(data?.message || 'Could not submit');
        setSubmitting(false);
      }
    } catch (err) {
      setSubmitError(errMessage(err, 'Could not submit'));
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="ob-shell">
        <div className="ob-container">
          <div className="ob-card">Loading…</div>
        </div>
      </div>
    );
  }

  if (phase === 'terminal') {
    return (
      <TerminalScreen
        title={terminal.title}
        message={terminal.message}
        kind={terminal.kind}
      />
    );
  }

  if (phase === 'submitted') {
    return (
      <TerminalScreen
        kind="success"
        title="Thanks — your submission is being reviewed"
        message={`An administrator will email you at ${
          invite?.inviteeEmail || 'the address you provided'
        } once your submission has been approved.`}
      />
    );
  }

  if (phase === 'approved') {
    return (
      <TerminalScreen
        kind="success"
        title="You're approved"
        message="Check your email for your GreenCharge vehicle PIN — you can start charging right away."
      />
    );
  }

  const docsRequested =
    Array.isArray(invite?.requestedDocuments) && invite.requestedDocuments.length > 0;
  const headerTitle = step === 1 ? 'Welcome' : `Step ${step} of ${steps.length}`;
  const headerSubtitle =
    invite?.inviteeFirstName || invite?.inviteeLastName || invite?.inviteeName
      ? `For ${[invite.inviteeFirstName, invite.inviteeLastName].filter(Boolean).join(' ') || invite.inviteeName}`
      : null;

  return (
    <WizardChrome
      brandTag="Onboarding"
      headerTitle={headerTitle}
      headerSubtitle={headerSubtitle}
      steps={steps}
      step={step}
      savingDraft={savingDraft}
      savedAt={savedAt}
      onBack={goBack}
      onNext={goNext}
      showNext={step < steps.length}
    >
      {currentStep?.id === 'welcome' && (
        <StepWelcome
          expiresAt={invite?.expiresAt}
          extraBullets={docsRequested ? ["Upload the documents your administrator has requested."] : []}
        />
      )}
      {currentStep?.id === 'contact' && (
        <StepContact payload={payload} updateField={updateField} />
      )}
      {currentStep?.id === 'vehicle' && (
        <StepVehicle payload={payload} updateField={updateField} />
      )}
      {currentStep?.id === 'documents' && (
        <StepDocuments
          requestedDocuments={invite?.requestedDocuments || []}
          documents={documents}
          pendingDocType={pendingDocType}
          setPendingDocType={setPendingDocType}
          fileInputRef={fileInputRef}
          uploading={uploading}
          uploadError={uploadError}
          onFileChosen={onFileChosen}
          onDeleteDoc={onDeleteDoc}
          onPreviewDoc={onPreviewDoc}
        />
      )}
      {currentStep?.id === 'review' && (
        <StepReview
          payload={payload}
          documents={documents}
          requestedDocuments={invite?.requestedDocuments || []}
          submitError={submitError}
          submitting={submitting}
          validationErrors={validationErrors}
          onSubmit={handleSubmit}
          onJumpToStep={jumpToStep}
          setAgreedToTerms={setAgreedToTerms}
        />
      )}
    </WizardChrome>
  );
}

// ── Documents step (personal-invite only) ────────────────────────────

function StepDocuments({
  requestedDocuments,
  documents,
  pendingDocType,
  setPendingDocType,
  fileInputRef,
  uploading,
  uploadError,
  onFileChosen,
  onDeleteDoc,
  onPreviewDoc,
}) {
  const allowedTypes = Array.isArray(requestedDocuments) && requestedDocuments.length > 0
    ? DOC_TYPES.filter((t) => requestedDocuments.includes(t.value))
    : DOC_TYPES;
  return (
    <>
      <h2>Supporting documents</h2>
      <p className="ob-subtle">
        Upload the documents your administrator has requested. Allowed: JPG, PNG, WebP, PDF
        up to 10 MB each.
      </p>
      <div className="ob-grid-2">
        <label className="ob-field">
          <span className="ob-field__label">Document type</span>
          <select
            className="ob-field__input"
            value={allowedTypes.some((t) => t.value === pendingDocType) ? pendingDocType : allowedTypes[0]?.value}
            onChange={(e) => setPendingDocType(e.target.value)}
          >
            {allowedTypes.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </label>
        <label className="ob-field">
          <span className="ob-field__label">File</span>
          <input
            ref={fileInputRef}
            className="ob-field__input"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            disabled={uploading}
            onChange={onFileChosen}
          />
        </label>
      </div>
      {uploadError && <p className="ob-alert ob-alert--warn">{uploadError}</p>}
      {documents.length > 0 ? (
        <ul className="ob-doc-list">
          {documents.map((d) => (
            <li key={d.id}>
              <div>
                <strong>{DOC_TYPES.find((x) => x.value === d.doc_type)?.label || d.doc_type}</strong>
                <div className="ob-subtle ob-subtle--small">
                  {d.original_filename} · {Math.round((d.size_bytes || 0) / 1024)} KB
                </div>
              </div>
              <div className="ob-doc-actions">
                <button className="ob-btn ob-btn--ghost" onClick={() => onPreviewDoc(d.id)}>Preview</button>
                <button className="ob-btn ob-btn--ghost ob-btn--danger" onClick={() => onDeleteDoc(d.id)}>Remove</button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ob-subtle">No documents uploaded yet.</p>
      )}
    </>
  );
}

export default OnboardingFlow;
